import React, { useState, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import { Search, PencilSquare, Trash, PlusCircle, Backspace, People, FileText, Copy } from "react-bootstrap-icons" // Iconos de Bootstrap
import { AlertError, AlertLoading } from '../helpers/AlertHelper'
import { formatDate } from "../helpers/momentHelper"
import formatCurrency from "../helpers/currencyHelper"
import FormModal from "../components/modals/FormModal"
import TableManagementThreeButtons from "../components/tables/TableManagementThreeButtoms";
import SearchSelect from '../components/form/SearchSelect'
import InvoicesService from "../services/InvoicesService"
import CustomersService from "../services/CustomersService"
import InvoicesForm from "./InvoicesForm"

const Invoices = () => {
    const intl = useIntl()
    const [invoiceId, setInvoiceId] = useState(null)
    const [invoices, setInvoices] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [currPage, setCurrPage] = useState(1)
    const [search, setSearch] = useState("")
    const [sortBy, setSortBy] = useState("date")
    const [sortOrder, setSortOrder] = useState("asc")
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(20)

    const [customersList, setCustomersList] = useState([])
    const [selectedCustomer, setSelectedCustomer] = useState('')

    // Setting modal title
    const [modalTitle, setModalTitle] = useState(false)

    // Setting modal icon
    const [modalIcon, setModalIcon] = useState(false)

    // Open modal form control to add/edit element
    const [modalTrigger, setModalTrigger] = useState(false)

    // Setting modal action enum[add,edit,delete]
    const [modalAction, setModalAction] = useState(false)

    // Fetch customers
    const fetchCustomers = async () => {
        try {
            const response = await CustomersService.getCustomers(1, '', 2000)
            setCustomersList(response.data.customers)
        } catch (err) {
            setError(err)
        }
    }

    // Fetch invoices
    const fetchInvoices = async () => {
        try {
            const response = await InvoicesService.getInvoices(currPage, search, perPage, sortBy, sortOrder, selectedCustomer)
            setInvoices(response.data.invoices)
            setTotalRows(response.data.pagination.totalItems)
            setIsLoaded(true)
        } catch (err) {
            setIsLoaded(false)
            setError(err)
        }
    }

    const handleModal = () => {
        setModalTrigger(false)
        fetchInvoices()
    }

    const handleSelectCustomerChange = (e) => {
        setSelectedCustomer(e.target.value)
        setCurrPage(1)
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    const handleSearchClean = () => {
        setSearch('')
        setSelectedCustomer('')
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    // fetch customers when start
    useEffect(() => {
        fetchCustomers()
    }, [])

    // Re-fetch data on page/perPage change
    useEffect(() => {
        fetchInvoices()
    }, [currPage, perPage, search, sortBy, sortOrder, selectedCustomer])

    const handleButtonClick = ({ data, type }) => {
        // Definir un objeto para mapear tipos a títulos e íconos
        const modalConfig = {
            add: {
                title: <FormattedMessage id="invoicesAdd" defaultMessage="Add invoice" />,
                icon: <PlusCircle />,
            },
            edit: {
                title: <FormattedMessage id="invoicesEdit" defaultMessage="Edit invoice" />,
                icon: <PencilSquare />,
            },
            delete: {
                title: <FormattedMessage id="invoicesDelete" defaultMessage="Delete invoice" />,
                icon: <Trash />,
            },
            duplicate: {
                title: <FormattedMessage id="textDuplicate" defaultMessage="Duplicate expense" />,
                icon: <Copy />,
            },
        }

        // Establecer la configuración del modal dependiendo del tipo
        const config = modalConfig[type] || {}

        if (config) {
            setInvoiceId(type === 'add' ? false : data._id) // Si 'add', invoiceid será falso
            setModalTitle(config.title) // Set title from the config
            setModalIcon(config.icon) // Set icon from the config
            setModalAction(type)
            setModalTrigger(true)
        }
    }

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage)
        setCurrPage(page)
    }

    const handleSort = async (column, sortDirection) => {
        setSortOrder(sortDirection)
        setSortBy(column.id)
        setCurrPage(1)
    }

    const columns = [
        {
            id: 'invoiceNumber',
            name: <FormattedMessage id="invoicesNumber" defaultMessage="Invoice number" />,
            selector: (row) => row.invoiceNumber,
            sortable: true,
        },
        {
            id: 'concept',
            name: <FormattedMessage id="invoicesConcept" defaultMessage="Concept" />,
            selector: (row) => row.concept,
        },
        {
            id: 'customerId',
            name: <FormattedMessage id="invoicesCustomer" defaultMessage="Customer" />,
            selector: (row) => row.customerId.name,
            sortable: true,
        },
        {
            id: 'date',
            name: <FormattedMessage id="invoicesDate" defaultMessage="Invoice date" />,
            selector: (row) => formatDate(row.date, 'DD/MM/YYYY'),
            sortable: true,
        },
        {
            id: 'estimatedDate',
            name: <FormattedMessage id="invoicesEstimatedDate" defaultMessage="Estimated date" />,
            selector: (row) => formatDate(row.estimatedDate, 'DD/MM/YYYY'),
            sortable: true,
        },
        {
            id: 'amount',
            name: <FormattedMessage id="invoicesAmount" defaultMessage="Amount" />,
            selector: (row) => formatCurrency(row.amount),
            sortable: true,
            right: "true",
        }
    ]


    return (
        <Container fluid className="p-0">
            <Row className="p-1 bg-secondary">
                <Col>
                    <InputGroup>
                        <InputGroup.Text><People /></InputGroup.Text>
                        <SearchSelect
                            fieldName={'customerId'}
                            selected={selectedCustomer}
                            onChange={handleSelectCustomerChange}
                            options={customersList}
                        />
                        <InputGroup.Text><Search /></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder={intl.formatMessage({ id: "textSearch", defaultMessage: "Search invoices..." })}
                            value={search}
                            onChange={handleSearchChange}
                        />
                        <Button onClick={handleSearchClean} variant="light">
                            <Backspace /> <FormattedMessage id="textClean" defaultMessage="Clean" />
                        </Button>

                        <Button onClick={() => handleButtonClick({ data: null, type: 'add' })} variant="primary">
                            <PlusCircle /> <FormattedMessage id="textAdd" defaultMessage="Add" />
                        </Button>
                    </InputGroup>
                </Col>
            </Row>
            {isLoaded ? (
                <TableManagementThreeButtons
                    icon={<FileText />}
                    title={<FormattedMessage id="navInvoices" defaultMessage="Invoices" />}
                    columns={columns}
                    data={invoices}
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={setCurrPage}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    onActionClick={handleButtonClick}
                />
            ) : error ? (
                <AlertError error={error} />
            ) : (
                <AlertLoading isLoaded={isLoaded} />
            )}

            <FormModal
                title={modalTitle}
                icon={modalIcon}
                body={InvoicesForm}
                refreshData={handleModal}
                modalTrigger={modalTrigger}
                elementId={invoiceId}
                action={modalAction} />

        </Container>
    )
}

export default Invoices